import React, { useEffect, useState } from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
} from "@mui/lab";
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  capitalizeWord,
  convertToUTCString,
} from "../../../utils/helpers/helper";

const DisplayTimeline = ({ timeline, selectedData: data, moduleName }) => {
  const [expandedDates, setExpandedDates] = useState([]);

  // Ensure actionStack exists in the timelineData
  const timelineData = timeline?.actionStack || [];

  // Group timeline data by date (remove time part)
  const groupByDate = (data) => {
    return (
      data?.length > 0 &&
      data.reduce((acc, entry) => {
        // Format date to YYYY-MM-DD (removes time part)
        const date = new Date(entry.date).toISOString().split("T")[0];
        if (!acc[date]) acc[date] = [];
        acc[date].push(entry);
        return acc;
      }, {})
    );
  };

  const handleExpand = (date) => {
    setExpandedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const reversedTimelineData =
    timelineData?.length > 0 && [...timelineData].reverse();
  const groupedTimelineData = groupByDate(reversedTimelineData);

  // Auto-expand all accordions when grouped data is available
  useEffect(() => {
    if (groupedTimelineData) {
      setExpandedDates(Object.keys(groupedTimelineData));
    }
  }, [timelineData]);

  return (
    <>
      {timelineData?.length > 0 ? (
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="d-lg-flex justify-content-between align-items-center">
                <h5 className="card-title">
                  Timeline Details for {moduleName.slice(0, -1)}
                  <span className="ms-1">
                    {capitalizeWord(data?.firstName)} {data?.lastName}
                  </span>
                </h5>

                <div className="d-flex gap-2 justify-content-between mb-1 text-capitalize">
                  <strong>Last Updated By:</strong>
                  <div className="d-flex flex-column align-items-start">
                    <span>{data?.lastUpdationBy?.name || "N/A"}</span>
                    <span className="text-muted">
                      {convertToUTCString(data?.lastActivityDate)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                {Object.keys(groupedTimelineData).map((date, dateIndex) => (
                  <Accordion
                    key={dateIndex}
                    expanded={expandedDates.includes(date)}
                    onChange={() => handleExpand(date)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "#f1f1f1",
                        fontWeight: "bold",
                        color: "#1976d2",
                      }}
                    >
                      {new Date(date).toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Timeline
                        sx={{
                          [`& .${timelineOppositeContentClasses.root}`]: {
                            flex: 0.2,
                          },
                        }}
                      >
                        {groupedTimelineData[date].map((entry, index) => (
                          <TimelineItem key={entry._id || index}>
                            <TimelineOppositeContent
                              sx={{ m: "auto 0" }}
                              align="right"
                              variant="body2"
                              color="textSecondary"
                            >
                              {new Date(entry.date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "UTC",
                                }
                              )}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot color="primary" />
                              {index < groupedTimelineData[date].length - 1 && (
                                <TimelineConnector />
                              )}
                            </TimelineSeparator>
                            <TimelineContent sx={{ py: "12px", px: 2 }}>
                              <Box>
                                <div className="text-capitalize text-black-50">
                                  {entry.actionMsg &&
                                  entry.actionMsg.includes(", ") ? (
                                    <ul className="custom-list">
                                      {entry.actionMsg
                                        .split(", ")
                                        .map((item, index) => (
                                          <li
                                            key={index}
                                            dangerouslySetInnerHTML={{
                                              __html: item,
                                            }}
                                          ></li>
                                        ))}
                                    </ul>
                                  ) : (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          entry.actionMsg ||
                                          "No activity available.",
                                      }}
                                    ></span>
                                  )}
                                </div>

                                {entry.by && (
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    By: {entry.by.name} ({entry.by.email})
                                  </Typography>
                                )}
                                {entry.to && (
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Assigned to: {entry.to.name} (
                                    {entry.to.email})
                                  </Typography>
                                )}
                              </Box>
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Timeline Details</h5>
              <p className="card-text">
                No description found for this timeline. Select data to view more
                details.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DisplayTimeline;
